/**
 * Dialog đăng nhập hai bước (email → mật khẩu).
 *
 * Hỗ trợ đăng nhập Google/Apple qua SocialAuthButtons.
 * Callback onSuccess chạy sau khi xác thực thành công.
 */
import { useEffect, useState } from 'react';
import { ButtonSpinner } from './ButtonSpinner';
import { BrandMark } from './BrandMark';
import { ErrorMessage } from './ErrorMessage';
import { ActionDialog } from './ActionDialog';
import { SocialAuthButtons } from './SocialAuthButtons';
import { useAuth } from '../../hooks/useAuth';
import { getApiErrorMessage } from '../../shared/utils/apiErrors';
import { UI_MESSAGES } from '../../shared/constants/messages';
import { TEXT } from '../../shared/languages';
import { isAppleAuthEnabled, isGoogleAuthEnabled } from '../../shared/utils/socialAuth';

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Modal đăng nhập.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} [props.onSuccess]
 * @param {Function} [props.onSwitchToRegister]
 */
export const LoginDialog = ({ isOpen, onClose, onSuccess, onSwitchToRegister }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, loginWithGoogle, loginWithApple } = useAuth();
  const t = TEXT.auth;
  const hasSocialAuth = isGoogleAuthEnabled() || isAppleAuthEnabled();

  useEffect(() => {
    if (!isOpen) {
      setStep('email');
      setEmail('');
      setPassword('');
      setError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    if (submitting) {
      return;
    }

    setError('');
    onClose();
  };

  const handleAuthSuccess = async (authAction) => {
    setSubmitting(true);
    setError('');

    try {
      await authAction();
      onSuccess?.();
      onClose();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, UI_MESSAGES.loginError));
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = (event) => {
    event.preventDefault();
    setError('');

    if (!email.trim()) {
      return;
    }

    setStep('password');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await handleAuthSuccess(() => login(email, password));
  };

  const handleGoogleSuccess = (idToken) => {
    handleAuthSuccess(() => loginWithGoogle(idToken));
  };

  const handleAppleSuccess = ({ idToken, name }) => {
    handleAuthSuccess(() => loginWithApple(idToken, name));
  };

  const toolbarStart =
    step === 'password' ? (
      <button
        type="button"
        className="auth-dialog-toolbar-back"
        onClick={() => setStep('email')}
        aria-label={TEXT.common.back}
      >
        <BackIcon />
      </button>
    ) : null;

  return (
    <ActionDialog
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={t.loginOrSignup}
      layout="airbnb"
      panelClassName="action-dialog-panel--auth"
      toolbarStart={toolbarStart}
    >
      <div className="auth-dialog-head">
        <BrandMark className="brand-mark--compact" />
        <div className="auth-dialog-head-copy">
          <h2 className="auth-dialog-title">
            {step === 'email' ? t.loginOrSignup : t.welcomeBack}
          </h2>
          {step === 'password' && (
            <p className="auth-dialog-subtitle">{email}</p>
          )}
        </div>
      </div>

      {step === 'email' ? (
        <>
          <form className="auth-dialog-form" onSubmit={handleContinue}>
            <ErrorMessage message={error} />
            <input
              className="auth-dialog-input"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t.emailPlaceholder}
              autoComplete="email"
              required
            />
            <button type="submit" className="auth-dialog-submit" disabled={submitting}>
              {t.continue}
            </button>
          </form>

          {hasSocialAuth && (
            <>
              <div className="auth-dialog-divider">
                <span>{t.orDivider}</span>
              </div>
              <SocialAuthButtons
                active={isOpen}
                disabled={submitting}
                onGoogleSuccess={handleGoogleSuccess}
                onAppleSuccess={handleAppleSuccess}
                onError={setError}
              />
            </>
          )}

          <p className="auth-dialog-footer-note">
            {t.noAccount}{' '}
            <button
              type="button"
              className="auth-dialog-footer-link"
              onClick={() => {
                onClose();
                onSwitchToRegister?.();
              }}
            >
              {t.registerFree}
            </button>
          </p>
        </>
      ) : (
        <form className="auth-dialog-form" onSubmit={handleSubmit}>
          <ErrorMessage message={error} />
          <input
            className="auth-dialog-input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={t.passwordPlaceholder}
            autoComplete="current-password"
            required
            autoFocus
          />
          <button type="submit" className="auth-dialog-submit" disabled={submitting}>
            {submitting && <ButtonSpinner />}
            {submitting ? t.loginSubmitting : t.loginTitle}
          </button>
        </form>
      )}
    </ActionDialog>
  );
};
